import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { formatCHF } from "@/lib/api";
import { useNoIndex } from "@/lib/seo";
import { useSeo } from "@/lib/seo";

const Confirmation = () => {
  useSeo({ title: "Confirmation · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const { state } = useLocation();
  const order = state?.order;

  useEffect(() => {
    document.title = "Commande confirmée — Entre les Mondes";
  }, []);

  if (!order) {
    return (
      <div className="pt-44 pb-32 text-center">
        <p className="font-serif text-3xl text-bark mb-6">Aucune commande récente à afficher.</p>
        <Link to="/boutique" data-testid="confirmation-boutique" className="link-line text-terra">Retour à la boutique</Link>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-24">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <CheckCircle2 className="w-16 h-16 text-sage mx-auto mb-8" />
        </motion.div>
        <p className="eyebrow mb-4">Commande confirmée</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-bark leading-tight" data-testid="confirmation-title">
          Merci, {order.client.nom.split(" ")[0]}.
        </h1>
        <p className="mt-5 text-fumee leading-relaxed">
          Votre commande <span className="font-mono text-terra" data-testid="confirmation-numero">{order.numero}</span> est confirmée.
          Un courriel récapitulatif arrive vers {order.client.email}. Votre tambour sera préparé avec soin et expédié sous 3 à 5 jours ouvrés.
        </p>
        <div className="mt-10 bg-card border border-sand-dark rounded-2xl p-8 text-left" data-testid="confirmation-recap">
          {order.lignes.map((l) => (
            <div key={l.product_id} className="flex justify-between text-sm py-2">
              <span className="text-bark">{l.nom} <span className="text-fumee">({l.numero})</span></span>
              <span className="text-bark">{formatCHF(l.prix)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm text-fumee py-2 border-t border-sand-dark mt-2">
            <span>Livraison</span><span>Offerte</span>
          </div>
          <div className="flex justify-between items-baseline py-2">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee">{order.paiement === "carte_cadeau" ? "Total (réglé par carte cadeau)" : "Total (règlement par facture)"}</span>
            <span className="font-serif text-2xl text-terra">{formatCHF(order.total)}</span>
          </div>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/boutique" data-testid="confirmation-cta-boutique"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300">
            Continuer la visite <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/agenda" data-testid="confirmation-cta-agenda"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-wood/30 text-bark text-sm hover:border-terra hover:text-terra transition-colors duration-300">
            Voir l'agenda
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
